// COSTURILE — devizul unui calcul de detaliu (`/p/:id/costuri/:sol`, contract
// §8), plus rețetele și tarifele proiectului (A7: «Costuri: doar pe detaliu;
// până atunci, „după calculul de detaliu"»).
//
// Găzduiește pagina de rețete portată din structură, care poartă toată
// suprafața: cardurile estimării, tabelul devizului (getEstimare), catalogul
// de rețete și tarifele suprascrise (getRetete/saveRetete). Devizul e al
// spațiului rândului din cale (corpul lui); rețetele sunt ale proiectului.
// Fără id, se deschide devizul firului, dacă firul are un detaliu gata.
//
// FAȚADA NU AȘTEAPTĂ STRUCTURA: de când arhitectura din fir are fațadă,
// rândul «Anvelopă» stă pe devizul ei (`getDevizFatada`), iar structura se
// spune «în curs de procesare» cât se calculează.

import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { corecteazaDevizFatada, getDevizFatada, type CampCorectie, type DevizFatada } from "../api";
import { useRolProiect } from "../components/rol";
import ObsHost from "../structure/ObsHost";
import Retete, { FILE_COSTURI, type FilaCosturi } from "../structure/Retete";
import { hashFisa } from "../structure/legaturi";
import { useStructure } from "../structure/StructureContext";
import { useT } from "../i18n";
import { useFir } from "../nav/useFir";
import { useRegistru } from "../nav/Registru";
import { EroareSolutie } from "../nav/BlocSolutie";
import { caleCosturi, caleRand, eDeschis, elementulRandului, numeElement } from "../nav/selectie";

export default function Costuri() {
  const { projectId = "" } = useParams();
  const { solutie, cloneId, solutiaCurenta } = useStructure();
  const { registru, reincarca, eroare: eroareRegistru } = useRegistru();
  const { potScrie } = useRolProiect();
  const { ruta, fir, eroare, incarcat } = useFir();
  const t = useT();
  const fila = useSearchParams()[0].get("fila");

  // CE ȘTIE PROIECTUL (terenul și CUT-ul din dosar, soluțiile firului,
  // documentul planului) le adună SERVERUL pentru rezumatul de business case
  // (`control-plane/api/business_case.py`); pagina mai aduce doar devizul
  // fațadei, pe care îl și desenează.
  const randul = (id: string | null) =>
    (id && registru?.solutii.find((s) => s.id === id)) || null;
  const arhFir = randul(fir.architecture);

  // DEVIZUL FAȚADEI arhitecturii din fir, cerut din nou la fiecare revizie a
  // rândului (fațada refăcută, recompartimentarea). Nu așteaptă
  // `summary.cost_fatada`: soluțiile de dinaintea lui îl au numai după
  // prima schimbare de fațadă, iar devizul se socotește din document; fără
  // fațadă serverul răspunde 422 și rândul rămâne pe reper.
  const [devizFatada, setDevizFatada] = useState<DevizFatada | null>(null);
  // arhitectura al cărei deviz de fațadă a răspuns măcar o dată; cererile de
  // după, la o revizie, nu mai ascund pagina (v. `seIncarca`)
  const [fatadaCitita, setFatadaCitita] = useState<string | null>(null);
  const arhId = arhFir?.id ?? null;
  const revizieArh = arhFir?.updated_at ?? null;
  useEffect(() => {
    if (!arhId) { setDevizFatada(null); return; }
    let viu = true;
    getDevizFatada(projectId, arhId)
      .then((d) => { if (viu) setDevizFatada(d); })
      .catch(() => { if (viu) setDevizFatada(null); })
      .finally(() => {
        if (viu) setFatadaCitita(arhId);
      });
    return () => { viu = false; };
  }, [projectId, arhId, revizieArh]);

  // CORECȚIA OMULUI pe devizul fațadei: se salvează, devizul se cere din nou,
  // iar registrul (cifra din antet) se reîmprospătează
  const laCorectieFatada = useCallback(async (cheie: string, camp: CampCorectie,
                                              valoare: number | null) => {
    if (!arhId) return;
    await corecteazaDevizFatada(projectId, arhId, cheie, camp, valoare);
    setDevizFatada(await getDevizFatada(projectId, arhId));
    void reincarca();
  }, [projectId, arhId, reincarca]);

  // STRUCTURA ÎN LUCRU pe arhitectura din fir: orice rând de structură
  // deschis pe ea (schița, macro, detaliul)
  const structuraInLucru = !!arhId && !!registru?.solutii.some((s) =>
    s.discipline === "structure" && s.lineage.architecture === arhId && eDeschis(s.stare));
  // REZUMATUL SE ARATĂ ABIA CU TOT CE-I TREBUIE: registrul (soluția listei) și
  // devizul fațadei; restul faptelor le aduce serverul odată cu rezumatul. Un
  // registru care n-a putut fi citit nu mai vine: atunci nu se așteaptă.
  const seIncarca = (!incarcat && !eroareRegistru) || (!!arhId && fatadaCitita !== arhId);

  const elFir = registru && fir.structure ? elementulRandului(registru.solutii, fir.structure) : null;
  const detaliuFir = elFir?.fel === "solutie" && elFir.detaliu?.status === "ready"
    ? elFir.detaliu : null;
  if (!ruta.sol && detaliuFir) {
    return <Navigate replace to={caleCosturi(projectId, detaliuFir.id)} />;
  }

  const el = registru && solutiaCurenta
    ? elementulRandului(registru.solutii, solutiaCurenta.id) : null;
  const eDetaliuGata = solutiaCurenta?.faza === "detaliu" && solutiaCurenta.status === "ready";

  return (
    <ObsHost className="ob-main">
      <div className="ob-pagina" style={{ position: "relative" }}>
        {eroare && (
          <EroareSolutie fel={eroare}
                         href={detaliuFir ? caleCosturi(projectId, detaliuFir.id) : null} />
        )}
        {/* reading trap (kept from the old shell): the FULL estimate here is
            not the "comparable between solutions" cost in the registry —
            that one is cut to the same chapters on every solution */}
        <div className="ob-pas-antet" style={{ paddingTop: 0 }}>
          {el && <b style={{ color: "var(--ink)" }}>{numeElement(el)}</b>}
          {!seIncarca && (
            <span>{eDetaliuGata ? t("costuri.capcana")
              : devizFatada ? t(structuraInLucru ? "costuri.fatada_structura_in_lucru"
                : "costuri.fatada_inainte_de_structura")
                : t("costuri.dupa_detaliu")}</span>
          )}
          <span className="ob-pas-nav">
            {solutiaCurenta && (
              <a href={caleRand(projectId, solutiaCurenta)}>{t("costuri.vezi_solutia")}</a>
            )}
            {eDetaliuGata && <a href={hashFisa(projectId, solutie)}>{t("costuri.fisa")}</a>}
          </span>
        </div>
        <Retete projectId={projectId} devizFatada={devizFatada}
                seIncarca={seIncarca}
                laCorectieFatada={potScrie ? laCorectieFatada : undefined}
                // DEVIZUL ÎNTREGII SOLUȚII (toate tronsoanele și groapa
                // complexului), nu al corpului ales în Studio: SCD-ul, aria
                // utilă și veniturile rezumatului sunt ale proiectului întreg
                cloneId={eDetaliuGata ? solutiaCurenta?.id ?? cloneId : null}
                // LISTA DE CANTITĂȚI: pe detaliu când e gata, altfel pe
                // arhitectura din fir — structura ei e estimată pe m²
                boqSolutionId={eDetaliuGata ? solutiaCurenta?.id ?? cloneId : arhId}
                filaInitiala={FILE_COSTURI.includes(fila as FilaCosturi) ? fila as FilaCosturi : null} />
      </div>
    </ObsHost>
  );
}
